import axios from 'axios';
import { validatePhoneNumber } from '@spike/validations';
import { DuplicateLocationDetails } from '../../../model/location';

export type DuplicateDetailsField = keyof DuplicateLocationDetails;

export type DuplicateFieldErrors = Partial<Record<DuplicateDetailsField, string>>;

export interface DuplicateApiErrors {
    fieldErrors: DuplicateFieldErrors;
    generalError?: string;
}

const EMAIL_REGEX = /\S+@\S+\.\S+/;

const GENERIC_ERROR_MESSAGE = 'Unable to duplicate this location. Please try again.';

const normalizeName = (name: string): string => name.trim().toLowerCase();

/**
 * Validates the details of the new location. Same rules as the add location form,
 * plus the name must differ from the source location's name.
 */
export const validateDuplicateDetails = (
    details: DuplicateLocationDetails,
    sourceName: string,
    country: string
): DuplicateFieldErrors => {
    const errors: DuplicateFieldErrors = {};

    if (!details.businessName.trim()) {
        errors.businessName = 'Location name is required';
    } else if (normalizeName(details.businessName) === normalizeName(sourceName)) {
        errors.businessName = 'Choose a name different from the original location';
    }

    if (!details.email.trim()) {
        errors.email = 'Work email is required';
    } else if (!EMAIL_REGEX.test(details.email)) {
        errors.email = 'Please enter a valid email address';
    }

    if (!details.phone.trim()) {
        errors.phone = 'Phone number is required';
    } else {
        const phoneErrors = validatePhoneNumber(details.phone, country, 'phone');
        if (phoneErrors.length > 0) {
            errors.phone = phoneErrors[0].errorMessage;
        }
    }

    if (!details.addressLineOne.trim()) {
        errors.addressLineOne = 'Address line 1 is required';
    }
    if (!details.city.trim()) {
        errors.city = 'City is required';
    }
    if (!details.state.trim()) {
        errors.state = 'State is required';
    }
    if (!details.zipcode.trim()) {
        errors.zipcode = 'Zipcode is required';
    }
    if (!details.country.trim()) {
        errors.country = 'Country is required';
    }

    return errors;
};

// Backend keys (with or without the "address." prefix) mapped to the form fields
const API_FIELD_MAP: Record<string, DuplicateDetailsField> = {
    business_name: 'businessName',
    url_slug: 'businessName',
    email: 'email',
    phone: 'phone',
    address_line_one: 'addressLineOne',
    address_line_two: 'addressLineTwo',
    city: 'city',
    state: 'state',
    zipcode: 'zipcode',
    country: 'country'
};

const FIELD_LABELS: Record<DuplicateDetailsField, string> = {
    businessName: 'Location name',
    email: 'Work email',
    phone: 'Phone number',
    addressLineOne: 'Address line 1',
    addressLineTwo: 'Address line 2',
    city: 'City',
    state: 'State',
    zipcode: 'Zipcode',
    country: 'Country'
};

// Rails sends messages without the subject ("has already been taken"), so the field label is prepended to those
const toFieldMessage = (field: DuplicateDetailsField, message: string): string =>
    /^[a-z]/.test(message) ? `${FIELD_LABELS[field]} ${message}` : message;

// "marketplace.address.city" → "city"
const stripPrefix = (key: string): string => key.replace(/^(marketplace\.)?(address\.)?/, '');

const firstMessage = (value: unknown): string | undefined => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    return undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Maps a 422 response of the duplicate endpoint to form field errors.
 * Accepts `{ field: [msg] }` and `{ errors: { field: [msg] } }`; errors that match no field become the general error.
 */
export const toDuplicateFieldErrors = (apiError: unknown): DuplicateApiErrors => {
    const data: unknown = axios.isAxiosError(apiError) ? apiError.response?.data : undefined;

    if (!isRecord(data)) {
        return { fieldErrors: {}, generalError: GENERIC_ERROR_MESSAGE };
    }

    const source = isRecord(data.errors) ? data.errors : data;
    const fieldErrors: DuplicateFieldErrors = {};
    const generalMessages: Array<string> = [];

    Object.entries(source).forEach(([key, value]) => {
        const message = firstMessage(value);
        if (!message) return;

        const field: DuplicateDetailsField | undefined = API_FIELD_MAP[stripPrefix(key)];

        if (field && !fieldErrors[field]) {
            fieldErrors[field] = toFieldMessage(field, message);
        } else if (!field) {
            // "error" / "message" already hold a full sentence, field errors need the field name
            generalMessages.push(
                key === 'error' || key === 'message' ? message : `${stripPrefix(key).replace(/_/g, ' ')} ${message}`
            );
        }
    });

    const hasFieldErrors = Object.keys(fieldErrors).length > 0;

    return {
        fieldErrors,
        generalError:
            generalMessages.length > 0 ? generalMessages.join('. ') : hasFieldErrors ? undefined : GENERIC_ERROR_MESSAGE
    };
};
